import React, { Component, ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { Video } from 'expo-av';
import Text from './shared/Text';
import Image from './shared/Image';
import { BASE_COLOR, SECONDARY_COLOR, GOLDEN200, WINDOW_HEIGHT, pOrT } from './styleConstants';

import { t } from '../utils/appUtils';
import { IS_WEB } from '../lib/appConstants';

const AppIntro = (IS_WEB ? require('../lib/AppIntroWeb') : require('../lib/AppIntro')).default; // eslint-disable-line

export const VIDEO_DELTA = 2068 / 1260;

type CalcVideoSizeArgs = {
  descHeight: number;
  offset: number;
  maxHeight?: number;
};

export function calcVideoSize({ descHeight, offset, maxHeight }: CalcVideoSizeArgs) {
  let videoHeight = WINDOW_HEIGHT - descHeight - offset;
  if (maxHeight) videoHeight = Math.min(videoHeight, maxHeight);
  return { videoHeight, videoWidth: videoHeight * VIDEO_DELTA };
}

const SLIDES = [
  {
    name: 'hero',
    videoRequire: require('../assets/videos/heroIntro.mp4'),
    textKey: 'intro.introHero',
  },
  {
    name: 'inventory',
    videoRequire: require('../assets/videos/inventoryIntro.mp4'),
    textKey: 'intro.introInventory',
  },
  {
    name: 'buildings',
    videoRequire: require('../assets/videos/buildingsIntro.mp4'),
    textKey: 'intro.introBuildings',
  },
  {
    name: 'island',
    videoRequire: require('../assets/videos/islandIntro.mp4'),
    textKey: 'intro.introIsland',
  },
  {
    name: 'combat',
    videoRequire: require('../assets/videos/combatIntro.mp4'),
    textKey: 'intro.introCombat',
  },
];

type VideoPlayerProps = {
  source: any;
  videoWidth: number;
  videoHeight: number;
  paused: boolean;
};

class VideoPlayer extends Component<VideoPlayerProps> {
  videoRef: any;

  // eslint-disable-next-line
  componentWillReceiveProps(nextProps: VideoPlayerProps) {
    this.playPause(nextProps);
  }

  playPause(props: VideoPlayerProps = this.props) {
    if (IS_WEB) {
      if (props.paused) this.videoRef.pause();
      else this.videoRef.play();
    } else {
      if (props.paused) this.videoRef.pauseAsync();
      else this.videoRef.playAsync();
    }
  }

  render() {
    const { source, videoWidth, videoHeight } = this.props;
    return IS_WEB ? (
      <video
        ref={(ref) => {
          this.videoRef = ref;
        }}
        width={videoWidth}
        height={videoHeight}
        onLoadedData={() => {
          this.playPause();
        }}
        muted
        loop
      >
        <source src={source} type="video/mp4" />
        <track kind="captions" />
      </video>
    ) : (
      <Video
        ref={(ref) => {
          this.videoRef = ref;
        }}
        source={source}
        onLoad={() => {
          this.playPause();
        }}
        isLooping
        resizeMode={(Video as any).RESIZE_MODE_COVER}
        style={{
          flex: 1,
        }}
      />
    );
  }
}

type IntroSlidesProps = {
  videoWidth: number;
  videoHeight: number;
  descHeight: number;
  skipBtnLabel?: ReactNode;
  doneBtnLabel?: ReactNode;
  onSkipBtnClick?: () => void;
  onDoneBtnClick?: () => void;
  showSkipButton?: boolean;
  showDoneButton?: boolean;
  topOffset?: number;
  inModal?: boolean;
};

type IntroSlidesState = {
  videoPlayIndex: number;
};

export default class IntroSlides extends Component<IntroSlidesProps, IntroSlidesState> {
  state = {
    videoPlayIndex: 0,
  };

  render() {
    const {
      videoWidth,
      videoHeight,
      descHeight,
      skipBtnLabel,
      doneBtnLabel,
      onSkipBtnClick,
      onDoneBtnClick,
      showSkipButton = true,
      showDoneButton = true,
      topOffset = 0,
      inModal,
    } = this.props;
    const { videoPlayIndex } = this.state;

    const styles = StyleSheet.create({
      slide: { flex: 1 },
      text: {
        fontSize: pOrT(13, 20),
        textAlign: 'center',
        color: GOLDEN200,
      },
      videoWrapper: {
        width: videoWidth,
        height: videoHeight,
      },
      textWrapper: {
        marginTop: 10,
        height: descHeight,
        width: videoWidth,
      },
    });

    return (
      <View>
        {!inModal ? (
          <View style={{ width: videoWidth }}>
            <Image
              style={{
                width: videoWidth + 30,
                height: videoHeight + 30,
                position: 'absolute',
                marginLeft: -15,
                marginTop: -15 + topOffset,
              }}
              resizeMode="stretch"
              source={require('../assets/images/bigBar4.png')}
            />
            <Image
              style={{
                width: videoWidth,
                height: videoHeight,
                position: 'absolute',
                marginTop: topOffset,
              }}
              resizeMode="stretch"
              source={require('../assets/images/paper2.png')}
            />
          </View>
        ) : null}

        <View style={{ width: videoWidth, overflow: 'hidden' }}>
          <AppIntro
            dotColor={BASE_COLOR}
            customStyles={{
              full: {
                height: 80,
                width: 120,
                justifyContent: 'center',
                alignItems: 'center',
              },
            }}
            activeDotColor={SECONDARY_COLOR}
            rightTextColor={BASE_COLOR}
            leftTextColor={BASE_COLOR}
            defaultIndex={videoPlayIndex}
            videoWidth={videoWidth}
            inModal={inModal}
            showSkipButton={showSkipButton}
            showDoneButton={showDoneButton}
            skipBtnLabel={skipBtnLabel}
            doneBtnLabel={doneBtnLabel}
            nextBtnLabel={
              <Text
                bold
                style={{
                  color: GOLDEN200,
                  fontSize: pOrT(14, 16),
                }}
              >
                {t('next')}
              </Text>
            }
            onSlideChange={(index: number) => {
              this.setState({ videoPlayIndex: index });
            }}
            onSkipBtnClick={onSkipBtnClick}
            onDoneBtnClick={onDoneBtnClick}
          >
            {SLIDES.map((item, index) => (
              <View
                style={[styles.slide, topOffset ? { marginTop: topOffset } : null]}
                key={item.name}
              >
                <View style={styles.videoWrapper}>
                  <VideoPlayer
                    source={item.videoRequire}
                    paused={index !== videoPlayIndex}
                    videoWidth={videoWidth}
                    videoHeight={videoHeight}
                  />
                </View>
                <View style={styles.textWrapper}>
                  <Text bold style={styles.text}>
                    {t(item.textKey)}
                  </Text>
                </View>
              </View>
            ))}
          </AppIntro>
        </View>
      </View>
    );
  }
}
